'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { supabase } from '@/lib/supabase';
import Icon from '@/components/ui/AppIcon';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt?: string;
  featured_image_url?: string;
  reading_time?: number;
  view_count?: number;
  published_at: string;
  author_id: string;
  created_at: string;
  author_name?: string;
}

const BlogPage = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadBlogPosts();
  }, []);

  const loadBlogPosts = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('blog_posts')
        .select(`
          id,
          title,
          slug,
          content,
          excerpt,
          featured_image_url,
          reading_time,
          view_count,
          published_at,
          author_id,
          created_at,
          user_profiles!author_id (
            name
          )
        `)
        .eq('is_published', true)
        .order('published_at', { ascending: false });

      if (error) throw error;

      // Transform data to flatten author information
      const transformedPosts = (data || []).map((post: any) => ({
        ...post,
        author_name: post.user_profiles?.name || 'Unknown Author'
      }));

      setPosts(transformedPosts);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load blog posts';
      setError(errorMessage);
      console.error('Error loading blog posts:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getPostPreview = (content: string, maxLength: number = 200) => {
    // Remove markdown headers and formatting for preview
    const cleanContent = content
      .replace(/#{1,6}\s+/g, '') // Remove markdown headers
      .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold formatting
      .replace(/\*(.*?)\*/g, '$1') // Remove italic formatting
      .replace(/\[(.*?)\]\(.*?\)/g, '$1') // Remove links, keep text
      .replace(/\n\s*\n/g, ' ') // Replace double line breaks with space
      .replace(/\n/g, ' ') // Replace single line breaks with space
      .trim();

    if (cleanContent.length <= maxLength) {
      return cleanContent;
    }

    return cleanContent.slice(0, maxLength).trim() + '...';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-muted rounded w-1/3 mb-4"></div>
            <div className="h-4 bg-muted rounded w-2/3 mb-8"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-card rounded-lg border border-border p-6">
                  <div className="h-6 bg-muted rounded mb-3"></div>
                  <div className="h-4 bg-muted rounded mb-2"></div>
                  <div className="h-4 bg-muted rounded mb-4 w-3/4"></div>
                  <div className="h-3 bg-muted rounded w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-heading font-bold text-foreground mb-4">
            Space Optimization Blog
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Discover expert tips, innovative solutions, and inspiring ideas to maximize your living space
          </p>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-8">
            <div className="flex items-center">
              <Icon name="ExclamationTriangleIcon" size={20} className="text-destructive mr-2" />
              <p className="text-destructive">{error}</p>
            </div>
          </div>
        )}

        {/* Blog Posts Grid */}
        {posts?.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post) => (
              <article
                key={post.id}
                className="bg-card rounded-lg border border-border overflow-hidden hover:shadow-lg transition-shadow duration-200"
              >
                {/* Featured Image */}
                {post.featured_image_url && (
                  <div className="w-full h-48 overflow-hidden">
                    <img
                      src={post.featured_image_url}
                      alt={post.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                )}
                
                <div className="p-6">
                  {/* Post Meta */}
                  <div className="flex items-center text-sm text-muted-foreground mb-3 flex-wrap gap-2">
                    <div className="flex items-center">
                      <Icon name="UserIcon" size={16} className="mr-1" />
                      <span>{post.author_name}</span>
                    </div>
                    <div className="flex items-center">
                      <Icon name="CalendarIcon" size={16} className="mr-1" />
                      <span>{formatDate(post.published_at)}</span>
                    </div>
                    {post.reading_time && (
                      <div className="flex items-center">
                        <Icon name="ClockIcon" size={16} className="mr-1" />
                        <span>{post.reading_time} min</span>
                      </div>
                    )}
                  </div>

                  {/* Post Title */}
                  <h2 className="text-xl font-semibold text-foreground mb-3 line-clamp-2">
                    {post.title}
                  </h2>

                  {/* Post Preview - Use excerpt if available, otherwise content */}
                  <p className="text-muted-foreground text-sm leading-relaxed mb-4 line-clamp-3">
                    {post.excerpt || getPostPreview(post.content, 150)}
                  </p>

                  {/* Read More Link */}
                  <Link
                    href={`/blog/${post.slug}`}
                    className="inline-flex items-center text-primary hover:text-primary/80 font-medium text-sm transition-colors"
                  >
                    Read More
                    <Icon name="ArrowRightIcon" size={16} className="ml-1" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        ) : !loading && !error ? (
          <div className="text-center py-12">
            <Icon name="DocumentTextIcon" size={48} className="mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">No Blog Posts Yet</h3>
            <p className="text-muted-foreground">
              Check back soon for expert tips on space optimization!
            </p>
          </div>
        ) : null}

        {/* Call to Action */}
        {posts?.length > 0 && (
          <div className="mt-16 text-center">
            <div className="bg-primary/10 rounded-lg p-8">
              <h3 className="text-2xl font-semibold text-foreground mb-4">
                Ready to Optimize Your Space?
              </h3>
              <p className="text-muted-foreground mb-6">
                Get personalized AI-powered recommendations for your specific space
              </p>
              <Link
                href="/input-form"
                className="inline-flex items-center px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 font-medium transition-colors"
              >
                <Icon name="PlusCircleIcon" size={20} className="mr-2" />
                Create Your Plan
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogPage;